import PropTypes from "prop-types";
import {
  BillingSwitch,
  ChoosePlanModalContainer,
} from "./ChoosePlanModal.styles";
import { useEffect, useMemo, useState } from "react";
import { usePlans } from "features/Plans/hook/usePlans";
import { Loader } from "components/Loader/index";
import { contains, isLoggedIn } from "utils";
import { useDispatch } from "react-redux";
import { setTheme } from "Layout/slice";
import { useSearchParams } from "react-router-dom";
import {
  STRIPE_CANCEL_URL,
  STRIPE_SUCCESS_URL,
  THEME_TYPES,
} from "utils/constants";
import { ModernPlanCard } from "features/Plans/components/ModernPlanCard/index";
import { useNavigation } from "utils/navigate";
import {
  continuePremiumPlan,
  subscribeToPremiumPlan,
} from "features/Plans/service";
import { openNewTab, parseErrorMessage } from "utils/index";
import { toast } from "Layout/slice/index";
import { fetchUserDetails } from "Layout/slice/thunk";

export const ChoosePlanModal = ({ onHide, show = true }) => {
  const [isAnnual, setIsAnnual] = useState(true);
  const dispatch = useDispatch();

  const { navigateToDashboard } = useNavigation();
  const [searchParams] = useSearchParams();
  const planParam = searchParams.get("plan");

  const { isLoading, plans } = usePlans();
  const [duration, setDuration] = useState("yearly");
  const [planCheckoutId, setPlanCheckoutId] = useState("");

  useEffect(() => {
    if (!isLoggedIn()) {
      dispatch(setTheme(THEME_TYPES.LIGHT));
    }
    if (planParam !== null) {
      handleDurationchange(
        contains(planParam, "yearly") || duration === "yearly"
      );
      getPremiumPlan(planParam);
    }
  }, []);

  const handlePlanSubscription = async (planId) => {
    try {
      setPlanCheckoutId(planId);
      const params = {
        plan: planId,
        interval: duration,
        success_url: STRIPE_SUCCESS_URL,
        cancel_url: STRIPE_CANCEL_URL,
      };
      const response = await subscribeToPremiumPlan(params);
      if (response.url) {
        openNewTab(response.url, "_self");
      } else {
        dispatch(
          toast({
            type: "info",
            body: response.message,
          })
        );
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
    } catch (error) {
      dispatch(
        toast({
          type: "error",
          body: parseErrorMessage(error),
        })
      );
    } finally {
      setPlanCheckoutId("");
    }
  };

  const getPremiumPlan = async (planId) => {
    await handlePlanSubscription(planId);
  };

  const handleContinuePremiumPlan = async (planId) => {
    try {
      setPlanCheckoutId(planId);
      await continuePremiumPlan();
      dispatch(fetchUserDetails());
      navigateToDashboard();
    } catch (error) {
      dispatch(
        toast({
          type: "error",
          body: parseErrorMessage(error),
        })
      );
    } finally {
      setPlanCheckoutId("");
    }
  };

  const handleDurationchange = (value) => {
    if (value) {
      setDuration("yearly");
      setIsAnnual(true);
    } else {
      setDuration("monthly");
      setIsAnnual(false);
    }
  };

  const handleToggleChange = () => {
    const newIsAnnual = !isAnnual;
    setIsAnnual(newIsAnnual);
    setDuration(newIsAnnual ? "yearly" : "monthly");
  };

  const durationPlan = useMemo(() => {
    let durationPlans = [];
    Object.keys(plans).forEach((plan) => {
      if (plans[plan].interval === duration || plans[plan].interval === null) {
        durationPlans.push(plans[plan]);
      }
    });
    return durationPlans;
  }, [plans, duration]);

  const remainingPlans = durationPlan.slice(0);

  return (
    <ChoosePlanModalContainer onHide={onHide} show={show}>
      <div className="heading">
        <h2>Choose your plan</h2>
      </div>

      {isLoading ? (
        <Loader />
      ) : (
        <div className="plan-container">
          <div className="plan-toggle">
            <span>Monthly</span>
            <BillingSwitch
              type="switch"
              id="billing-switch"
              checked={isAnnual}
              onChange={handleToggleChange}
            />
            <span>Annually</span>
            <span className="text-save">Get 2 months free 🎉</span>
          </div>

          <div className="plan-wrapper">
            {remainingPlans.map((plan, inx) => (
              <ModernPlanCard
                plan={plan}
                key={inx}
                index={inx}
                duration={duration}
                getPremiumPlan={getPremiumPlan}
                planCheckoutId={planCheckoutId}
                handleContinuePremiumPlan={handleContinuePremiumPlan}
                handlePlanSubscription={handlePlanSubscription}
              />
            ))}
          </div>
        </div>
      )}
    </ChoosePlanModalContainer>
  );
};

ChoosePlanModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func,
  toggleFullcodeTemplates: PropTypes.func,
  toggleNocodeTemplates: PropTypes.func,
};

ChoosePlanModal.displayName = "CreateStrategyModal";
